<script setup lang="ts">
/**
 * /cv-languages: the "Translate" section of the CV editor
 * (components/CV/Editor/TranslatePanel.vue), explained. What it does, how,
 * what it leaves alone, and what is free. The feature lives in the editor, so
 * the buttons open the CV list or the sign-up.
 *
 * The anchor #translate is linked from the homepage (components/sofax/EditorExtrasSection.vue).
 */
import { SUPPORTED_CV_LANGUAGES } from '~/constants/cvLanguages'

const { t } = useI18n()
const { isAuthenticated } = useAuthUser()

const startPath = computed(() => (isAuthenticated.value ? '/cv' : '/registration'))
const languageCount = SUPPORTED_CV_LANGUAGES.length

const steps = [
  { id: 'open', icon: 'bi-file-earmark-text' },
  { id: 'pick', icon: 'bi-translate' },
  { id: 'check', icon: 'bi-files' },
] as const

const stays = ['names', 'dates', 'tools', 'design'] as const
const changes = ['texts', 'skills', 'headings'] as const
</script>

<template>
  <!-- What it is, with a before/after -->
  <section id="translate" class="sofax scroll-mt-24 bg-white dark:bg-gray-950">
    <div class="section-space">
      <div class="shell">
        <div class="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-20">
          <div>
            <div class="eyebrow mb-5">{{ t('translatePage.eyebrow') }}</div>
            <h2 class="mb-5 !text-3xl md:!text-[2.5rem]">{{ t('translatePage.title') }}</h2>
            <p class="!mb-5 text-lg">{{ t('translatePage.p1', { count: languageCount }) }}</p>
            <p class="mb-8">{{ t('translatePage.p2') }}</p>
            <SofaxBtn :to="startPath" :label="t('translatePage.cta')" variant="accent" />
          </div>

          <div class="mx-auto flex w-full max-w-[31.25rem] flex-col gap-4">
            <div class="card">
              <div class="mb-2 text-sm font-semibold uppercase tracking-wide text-muted dark:text-gray-400">
                🇨🇿 {{ t('translatePage.demo.fromLabel') }}
              </div>
              <div class="font-title font-bold text-ink dark:text-white">{{ t('translatePage.demo.fromTitle') }}</div>
              <p class="mt-1 text-base">{{ t('translatePage.demo.fromText') }}</p>
            </div>
            <div class="flex justify-center" aria-hidden="true">
              <SofaxIconTile icon="bi-arrow-down" round size="sm" />
            </div>
            <div class="card border-accent bg-accent-soft/20 dark:bg-accent/10">
              <div class="mb-2 text-sm font-semibold uppercase tracking-wide text-accent">
                🇬🇧 {{ t('translatePage.demo.toLabel') }}
              </div>
              <div class="font-title font-bold text-ink dark:text-white">{{ t('translatePage.demo.toTitle') }}</div>
              <p class="mt-1 text-base font-medium text-ink dark:text-white">{{ t('translatePage.demo.toText') }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- How it works -->
  <section class="sofax surface-alt">
    <div class="section-space">
      <div class="shell">
        <SofaxSectionHeading
          :eyebrow="t('translatePage.how.eyebrow')"
          :title="t('translatePage.how.title')"
          :text="t('translatePage.how.subtitle')"
        />
        <ol class="grid grid-cols-1 gap-6 md:grid-cols-3">
          <li
            v-for="(step, index) in steps"
            :key="step.id"
            class="h-full rounded-[0.625rem] bg-white p-[1.875rem] dark:bg-gray-800"
          >
            <div class="flex items-center gap-4">
              <SofaxIconTile :icon="step.icon" size="sm" />
              <span class="font-title text-sm font-bold text-muted dark:text-gray-400">{{ index + 1 }} / {{ steps.length }}</span>
            </div>
            <div class="display-heading display-heading-5 mb-3 mt-6">{{ t(`translatePage.how.steps.${step.id}.title`) }}</div>
            <p class="text-base">{{ t(`translatePage.how.steps.${step.id}.text`) }}</p>
          </li>
        </ol>
      </div>
    </div>
  </section>

  <!-- What stays, what changes, what is free -->
  <section class="sofax bg-white dark:bg-gray-950">
    <div class="section-space">
      <div class="shell">
        <div class="grid grid-cols-1 gap-8 lg:grid-cols-2">
          <div class="rounded-[0.625rem] bg-ink p-[1.875rem] text-white dark:bg-gray-800 md:p-10">
            <SofaxIconTile icon="bi-shield-check" round size="md" />
            <h2 class="mb-6 mt-8 !text-2xl !text-white md:!text-3xl">{{ t('translatePage.stays.title') }}</h2>
            <ul class="flex flex-col gap-4">
              <li v-for="item in stays" :key="item" class="flex items-start gap-3">
                <i class="bi bi-check-circle-fill mt-0.5 shrink-0 text-xl text-accent-soft" />
                <span class="text-base !text-white/85">{{ t(`translatePage.stays.items.${item}`) }}</span>
              </li>
            </ul>
          </div>

          <div class="flex flex-col gap-6">
            <div class="rounded-[0.625rem] bg-surface p-[1.875rem] dark:bg-gray-800">
              <div class="display-heading display-heading-4 mb-5">{{ t('translatePage.changes.title') }}</div>
              <ul class="flex flex-col gap-3">
                <li v-for="item in changes" :key="item" class="flex items-start gap-3">
                  <i class="bi bi-arrow-repeat mt-0.5 shrink-0 text-xl text-accent" />
                  <span class="text-base">{{ t(`translatePage.changes.items.${item}`) }}</span>
                </li>
              </ul>
            </div>
            <div class="rounded-[0.625rem] border-2 border-accent bg-white p-[1.875rem] dark:bg-gray-800">
              <div class="display-heading display-heading-4 mb-2">{{ t('translatePage.plans.title') }}</div>
              <p class="text-base">{{ t('translatePage.plans.text') }}</p>
              <div class="mt-5">
                <SofaxArrowLink to="/pricing" :label="t('translatePage.plans.pricingLink')" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
